/**
 * Validación compartida de los formularios de auth (login, signup, reset).
 *
 * Módulo puro (sin side-effects) para poder unit-testear las reglas de
 * email/contraseña sin montar la UI. Las pantallas y hooks consumen los
 * resultados y aplican los side-effects (setError en el store, toasts).
 */

import {
  EMAIL_REGEX as SHARED_EMAIL_REGEX,
  isValidEmail as sharedIsValidEmail,
  normalizeEmail as sharedNormalizeEmail,
} from '@shared/email';

export const EMAIL_REGEX = SHARED_EMAIL_REGEX;

export function isValidEmail(email: string): boolean {
  return sharedIsValidEmail(email);
}

export function isPasswordLongEnough(password: string): boolean {
  return password.length >= 8;
}

export type AuthFormValidationToast = {
  kind: 'info' | 'error';
  title: string;
  body: string;
};

export type AuthFormValidationResult =
  | { ok: true }
  | { ok: false; error: string; toast: AuthFormValidationToast };

/**
 * Validación de login: requiere email + contraseña con formato de correo válido.
 */
export function validateLoginInput(email: string, password: string): AuthFormValidationResult {
  const trimmedEmail = email.trim();
  if (!trimmedEmail || !password) {
    return {
      ok: false,
      error: 'Completá usuario y contraseña.',
      toast: { kind: 'info', title: 'Faltan datos', body: 'Completá usuario y contraseña.' },
    };
  }
  if (!isValidEmail(trimmedEmail)) {
    return {
      ok: false,
      error: 'Correo inválido.',
      toast: { kind: 'error', title: 'Email incorrecto', body: 'Revisá el formato del correo.' },
    };
  }
  return { ok: true };
}

/**
 * Validación de signup: misma base que login + contraseña de 8+ caracteres y
 * coincidencia con la repetición.
 */
export function validateSignupInput(
  email: string,
  password: string,
  passwordConfirm: string,
): AuthFormValidationResult {
  const base = validateLoginInput(email, password);
  if (!base.ok) return base;
  if (!isPasswordLongEnough(password)) {
    const msg = 'La contraseña debe tener al menos 8 caracteres.';
    return { ok: false, error: msg, toast: { kind: 'info', title: 'Contraseña', body: msg } };
  }
  if (password !== passwordConfirm) {
    const msg = 'Las contraseñas no coinciden.';
    return { ok: false, error: msg, toast: { kind: 'info', title: 'Contraseñas', body: msg } };
  }
  return { ok: true };
}

/**
 * Validación del flujo de recuperación de contraseña desde el login.
 */
export function validatePasswordResetInput(email: string): AuthFormValidationResult {
  const trimmed = email.trim();
  if (!trimmed) {
    return {
      ok: false,
      error: 'Ingresá tu correo',
      toast: {
        kind: 'info',
        title: 'Ingresá tu correo',
        body: 'Escribilo en el campo de arriba y volvé a tocar «¿Olvidaste tu contraseña?».',
      },
    };
  }
  if (!isValidEmail(trimmed)) {
    return {
      ok: false,
      error: 'Correo inválido',
      toast: { kind: 'error', title: 'Correo inválido', body: 'Revisá que el email esté bien escrito.' },
    };
  }
  return { ok: true };
}

/** Normaliza un correo para enviar al backend: quita invisibles, trim, lowercase. */
export function normalizeEmail(email: string): string {
  return sharedNormalizeEmail(email);
}
